import './duplicateData.less'
import { RootState } from '../../../../rootReducer'
import { getShowLoader } from '../../../../reducers/requestReducer'
import LoaderView from '../../../../components/Loader/loader'
import ChartBarSimple from '../../../../components/ChartBarSimple/chartBarSimple'
import { ChartColors, getUniqueWidgetIndex, UNIQUE_WIDGET_INDEX } from '../constants'
import {
  ACTIVE_TAB,
  DATA_SOURCE_ID,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_TYPES,
  DS_FILES_TABS,
  DUPLICATE_DATA_NOT_SUPPORTED_DATASOURCES,
  FILES_GROUP_BY,
  GROUP_BY,
  URL_DATA_SOURCES,
  URL_DOCUMENTS_CLASSIFIED,
  URL_FILES,
  URL_INSIGHTS
} from '../../../../constants'
import {
  ACTION_FETCH_DUPLICATE_DATA_WIDGET,
  DuplicateDataWidget,
  DuplicateDataWidgetParams,
  fetchDuplicateDataWidget
} from '../../../dataSources/dataSourcesSlice'
import useUrlContext from '../../../../hooks/useUrlContext'
import { humanDiskSize } from '../../../../utils/mathUtils'
import { getUpdatedUrl } from '../../../../utils/urlUtil'
import CustomTooltip from '../../../../components/ChartTooltipV2'
import { getWindowProps } from '../../../../utils'
import useWindowSize from '../../../../hooks/useWindowResize'
import { mockDuplicateDataWidgetData } from '../../renderer/utils/widgetMockdata'
import { connect } from 'react-redux'
import { FormattedNumber, useIntl } from 'react-intl'
import React, { useEffect } from 'react'
import { Typography } from '@lightbeamai/design-system'
import { useHistory, Link } from 'react-router-dom'
import { ArrowSquareOut as IconArrowSquareOut } from 'phosphor-react'

const MAX_COUNT_TO_SHOW = 5

interface OwnProps {
  useMockData?: boolean
  chartBarAlignment?: 'vertical' | 'horizontal'
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDatasourceType?: DATA_SOURCE_TYPES
}

interface StateProps {
  widget?: DuplicateDataWidget
  showLoader: boolean
}

interface DispatchProps {
  fetchDuplicateDataWidget: (
    params: DuplicateDataWidgetParams & { [UNIQUE_WIDGET_INDEX]?: string }
  ) => void
}

type DataDuplicationWidgetComponentProps = StateProps & DispatchProps & OwnProps

const DuplicateDataWidget = (props: DataDuplicationWidgetComponentProps) => {
  const {
    widget,
    showLoader,
    chartBarAlignment = 'horizontal',
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    fetchDuplicateDataWidget
  } = props
  const intl = useIntl()
  const history = useHistory()

  const context = useUrlContext()
  const datasourceId = widgetDatasourceId || context.contextFilters[DATA_SOURCE_ID] || ''

  const windowProps = getWindowProps(useWindowSize())
  const { isLargeScreen } = windowProps

  useEffect(() => {
    if (useMockData) return
    fetchDuplicateDataWidget({
      datasourceId,
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [useMockData, datasourceId, widgetDashboardId, widgetDatasourceId, fetchDuplicateDataWidget])

  const chartsData =
    (widget?.datasources || [])
      .filter(({ type }) => !DUPLICATE_DATA_NOT_SUPPORTED_DATASOURCES.includes(type))
      .map(({ id, name, type, totalDuplicateBytes }) => ({
        dataKey: JSON.stringify({ id, type }) || '',
        name: name || '',
        value: totalDuplicateBytes || 0,
        color: ChartColors.Blue
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, MAX_COUNT_TO_SHOW) || []

  const totals = widget?.datasources
    ?.filter(({ type }) => !DUPLICATE_DATA_NOT_SUPPORTED_DATASOURCES.includes(type))
    .reduce(
      (acc, ds) => {
        return {
          numDuplicateObjects: acc.numDuplicateObjects + (Number(ds.numDuplicateObjects) || 0),
          totalDuplicateBytes: acc.totalDuplicateBytes + (Number(ds.totalDuplicateBytes) ?? 0)
        }
      },
      { numDuplicateObjects: 0, totalDuplicateBytes: 0 }
    )

  const countsFormatter = (value) => intl.formatNumber(value, { notation: 'compact' })

  const dashboardLink = getUpdatedUrl({
    pathName: URL_INSIGHTS + URL_DOCUMENTS_CLASSIFIED,
    queryParams: { [GROUP_BY]: FILES_GROUP_BY.DUPLICATES }
  })

  const handleClick = (val) => {
    if (datasourceId) return

    const key = val?.dataKey || val?.payload?.dataKey
    if (!key) return

    const { id, type } = JSON.parse(key)

    const url = getUpdatedUrl({
      pathName: `${URL_DATA_SOURCES}/${id}${URL_FILES}`,
      pageName: URL_DOCUMENTS_CLASSIFIED,
      queryParams: {
        [ACTIVE_TAB]: DS_FILES_TABS.DUPLICATE_FILES,
        [DATA_SOURCE_TYPE]: type
      }
    })
    history.push(url)
  }

  return (
    <div
      className="lb-widget-card duplicate-data-widget xs-p-16"
      data-test-id="dashboard-duplicate-data-widget"
    >
      <div className="duplicate-data-widget-header">
        <Typography color="primary" variant="h6">
          {intl.formatMessage({ id: 'duplicateData' })}
        </Typography>
        <Link to={dashboardLink} className="dashboard-link" target={!datasourceId ? '_blank' : ''}>
          <Typography color="primary" type="medium" variant="h6">
            {intl.formatMessage({ id: 'duplicateData.viewAll' })}
          </Typography>
          <IconArrowSquareOut size="16" color="var(--color-primary-light)" weight="bold" />
        </Link>
      </div>

      <div className={`duplicate-data-chart-wrapper ${chartBarAlignment}`}>
        <div className="dashboard-labels-count-wrapper">
          <div className="label-items">
            <Link className="label-item" to={dashboardLink} target={!datasourceId ? '_blank' : ''}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber value={totals?.numDuplicateObjects || 0} notation="compact" />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({ id: 'duplicateData.files' })}
              </Typography>
            </Link>
            <Link className="label-item" to={dashboardLink} target={!datasourceId ? '_blank' : ''}>
              <Typography color="primary" type="strong" variant="h4">
                {humanDiskSize(totals?.totalDuplicateBytes || 0)}
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({ id: 'duplicateData.dataSize' })}
              </Typography>
            </Link>
          </div>
        </div>
        <div className="charts-wrapper duplicate-data-bar-charts">
          <Typography as="div" type="medium" variant="baseMinus2" className="xs-ml-48">
            {intl.formatMessage({ id: 'duplicateData.top5Ds' })}
          </Typography>
          <ChartBarSimple
            className="xs-mt-24 v2-chart"
            data={chartsData}
            cartesianGridHorrizontal
            tickFormatter={{
              fontSize: '0.72rem',
              fill: ChartColors.SecondaryText,
              width: 60,
              textAnchor: 'middle'
            }}
            multiColored
            chartMargin={{ left: 20, right: 20, top: 0, bottom: 0 }}
            barSize={60}
            chartResponsiveHeight={isLargeScreen ? 240 : 200}
            {...(isLargeScreen ? { xAxisHeight: 55 } : {})}
            yLabel={intl.formatMessage({ id: 'duplicateData.dataSize' })}
            xAxisAngle={0}
            YTickFormatter={countsFormatter}
            yAxisLabelPadding={{ dx: -12, dy: -6 }}
            customToolTip={
              <CustomTooltip title={intl.formatMessage({ id: 'duplicateData.dataSize' })} />
            }
            tickMargin={4}
            xAxisTickInterval={0}
            onBarItemClick={handleClick}
          />
        </div>
      </div>
      <LoaderView size="small" showLoader={showLoader} />
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? mockDuplicateDataWidgetData.widget
    : state.dataSources.widgetDuplicateData,
  showLoader: getShowLoader(ACTION_FETCH_DUPLICATE_DATA_WIDGET)
})

const mapDispatchToProps: DispatchProps = { fetchDuplicateDataWidget }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(DuplicateDataWidget)
